/**
 * Delivery Note API Client
 * Epic 63: Szallitolevol (Delivery Note) v2
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export type DeliveryNoteType = 'STANDARD' | 'CONTRACT' | 'RENTAL_ACCESSORY' | 'CREDIT';
export type DeliveryNoteStatus = 'OPEN' | 'INVOICED' | 'SETTLED' | 'CLOSED';

export interface DeliveryNoteItem {
  id: string;
  deliveryNoteId: string;
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  returnedQuantity: number;
}

export interface DeliveryNote {
  id: string;
  tenantId: string;
  noteNumber: string;
  type: DeliveryNoteType;
  status: DeliveryNoteStatus;
  partnerId?: string;
  partnerName: string;
  isContractPartner: boolean;
  items: DeliveryNoteItem[];
  totalAmount: number;
  invoiceId?: string;
  rentalContractId?: string;
  supplierCreditId?: string;
  notes?: string;
  settledManually: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface DeliveryNoteSummary {
  totalNotes: number;
  byType: Record<DeliveryNoteType, number>;
  byStatus: Record<DeliveryNoteStatus, number>;
  totalAmount: number;
}

export interface CreateDeliveryNoteInput {
  type: DeliveryNoteType;
  partnerName: string;
  partnerId?: string;
  rentalContractId?: string;
  notes?: string;
  items: Array<{
    productId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
  }>;
}

export interface DeliveryNoteFilters {
  type?: DeliveryNoteType;
  status?: DeliveryNoteStatus;
  partnerId?: string;
}

// ============================================
// API CALLS
// ============================================

export async function createDeliveryNote(input: CreateDeliveryNoteInput) {
  return api.post<{ data: DeliveryNote }>('/delivery-notes', input);
}

export async function listDeliveryNotes(filters?: DeliveryNoteFilters) {
  const params = new URLSearchParams();
  if (filters?.type !== undefined) params.set('type', filters.type);
  if (filters?.status !== undefined) params.set('status', filters.status);
  if (filters?.partnerId !== undefined) params.set('partnerId', filters.partnerId);
  const query = params.toString();
  return api.get<{ data: DeliveryNote[] }>(`/delivery-notes${query ? `?${query}` : ''}`);
}

export async function getDeliveryNote(id: string) {
  return api.get<{ data: DeliveryNote }>(`/delivery-notes/${id}`);
}

export async function getDeliveryNoteSummary() {
  return api.get<{ data: DeliveryNoteSummary }>('/delivery-notes/summary');
}

export async function linkToInvoice(id: string, noteIds: string[], invoiceId: string) {
  return api.patch<{ data: DeliveryNote[] }>(`/delivery-notes/${id}/invoice`, {
    noteIds,
    invoiceId,
  });
}

export async function markSettled(id: string) {
  return api.patch<{ data: DeliveryNote }>(`/delivery-notes/${id}/settle`, {});
}

// ============================================
// BATCH INVOICE (Gyujtoszamla)
// ============================================

export interface BatchInvoiceResult {
  invoice: {
    id: string;
    invoiceNumber: string;
    grossAmount: number;
  };
  linkedNoteIds: string[];
  linkedNoteCount: number;
  totalAmount: number;
}

export interface GenerateBatchInvoiceInput {
  noteIds: string[];
  paymentMethod?: string;
  dueDays?: number;
  notes?: string;
  vatRate?: string;
}

export interface MonthlyBatchResult {
  invoices: BatchInvoiceResult[];
  errors: Array<{ partnerId: string; partnerName: string; error: string }>;
  totalInvoicesCreated: number;
  totalNotesProcessed: number;
}

export async function generateBatchInvoice(input: GenerateBatchInvoiceInput) {
  return api.post<{ data: BatchInvoiceResult }>('/delivery-notes/generate-invoice', input);
}

export async function generateMonthlyBatch() {
  return api.post<{ data: MonthlyBatchResult }>('/delivery-notes/monthly-batch', {});
}
